import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Wrap async operations with consistent error handling
 */
export async function withMonitoring<T>(
  operation: string,
  fn: () => Promise<T>,
  context: {
    userId?: string;
    username?: string;
    sessionId?: string;
    entityType?: string;
    entityId?: string;
    action?: string;
    metadata?: Record<string, any>;
  }
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    console.error(`[${operation}] failed:`, error);
    throw error;
  }
}

/**
 * Format currency with proper locale support
 */
export function formatCurrency(
  amount: number | string, 
  currency = 'USD', 
  locale = 'en-US',
  decimalPlaces?: number
): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimalPlaces ?? 2,
    maximumFractionDigits: decimalPlaces ?? 2,
  }).format(numAmount);
}

/**
 * Format date with timezone support
 */
export function formatDate(
  date: Date | string, 
  format: 'full' | 'long' | 'medium' | 'short' = 'medium', 
  locale = 'en-US',
  timezone?: string
): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(locale, {
    dateStyle: format,
    timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(d);
}

/**
 * Format date and time
 */
export function formatDateTime(
  date: Date | string, 
  locale = 'en-US',
  timezone?: string
): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(d);
}

/**
 * Debounce function with immediate option
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate = false
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };

    const callNow = immediate && !timeout;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func(...args);
  };
}

/**
 * Throttle function with leading/trailing options
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number,
  options: { leading?: boolean; trailing?: boolean } = {}
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  let previous = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();

    if (!previous && options.leading === false) {
      previous = now;
    }

    const remaining = limit - (now - previous);

    if (remaining <= 0 || remaining > limit) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      func(...args);
    } else if (!timeout && options.trailing !== false) {
      timeout = setTimeout(() => {
        previous = options.leading === false ? 0 : Date.now();
        timeout = null;
        func(...args);
      }, remaining);
    }
  };
}

/**
 * Deep clone object with circular reference handling
 */
export function deepClone<T>(obj: T): T {
  const seen = new WeakMap();

  const clone = (value: any): any => {
    if (value === null || typeof value !== 'object') {
      return value;
    }

    if (seen.has(value)) {
      return seen.get(value);
    }

    if (Array.isArray(value)) {
      const arrCopy: any[] = [];
      seen.set(value, arrCopy);
      value.forEach((item, index) => {
        arrCopy[index] = clone(item);
      });
      return arrCopy;
    }

    if (value instanceof Date) {
      return new Date(value);
    }

    if (value instanceof RegExp) {
      return new RegExp(value);
    }

    if (value instanceof Map) {
      const mapCopy = new Map();
      seen.set(value, mapCopy);
      value.forEach((val, key) => {
        mapCopy.set(key, clone(val));
      });
      return mapCopy;
    }

    if (value instanceof Set) {
      const setCopy = new Set();
      seen.set(value, setCopy);
      value.forEach((val) => {
        setCopy.add(clone(val));
      });
      return setCopy;
    }

    const objCopy: Record<string, any> = {};
    seen.set(value, objCopy);

    Object.keys(value).forEach(key => {
      objCopy[key] = clone(value[key]);
    });

    return objCopy;
  };

  return clone(obj);
}

/**
 * Safely parse JSON with error handling and type safety
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    const parsed = JSON.parse(json);

    // Validate type if fallback is provided
    if (fallback !== undefined && typeof parsed !== typeof fallback) {
      return fallback;
    }

    return parsed;
  } catch (error) {
    console.warn('Failed to parse JSON:', error);
    return fallback;
  }
}

/**
 * Generate unique ID with prefix option
 */
export function generateId(prefix?: string): string {
  const id = crypto.randomUUID();
  return prefix ? `${prefix}_${id}` : id;
}

/**
 * Validate email address
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
}

/**
 * Validate phone number with international support
 */
export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^[\+]?[1-9][\d]{0,3}[-\s\.]?[(]?[\d]{1,4}[)]?[-\s\.]?[\d]{1,4}[-\s\.]?[\d]{1,9}$/;
  return phoneRegex.test(phone.trim());
}

/**
 * Format decimal numbers with proper precision
 */
export function formatDecimal(
  value: number | string,
  decimalPlaces = 2,
  locale = 'en-US'
): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(numValue);
}

/**
 * Convert object to query string
 */
export function toQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item.toString()));
      } else if (value instanceof Date) {
        searchParams.append(key, value.toISOString());
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });

  return searchParams.toString();
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number, ellipsis = '...'): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - ellipsis.length) + ellipsis;
}

/**
 * Calculate percentage
 */
export function calculatePercentage(part: number, total: number): number {
  if (total === 0) return 0;
  return (part / total) * 100;
}

/**
 * Sleep/delay function
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Capitalize first letter of each word
 */
export function capitalizeWords(str: string): string {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Generate random number in range
 */
export function randomInRange(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Merge objects deeply
 */
export function deepMerge<T extends Record<string, any>>(target: T, source: T): T {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          (output as any)[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }

  return output;
}

function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Generate a hash from string
 */
export function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}